// Keep this list explicit: names such as ref, source, campaign, and unknown
// utm_* parameters may identify a product, language, or configuration.
const TRACKING_QUERY_PARAMETERS = new Set([
  "utm_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_source_platform",
  "utm_creative_format",
  "utm_marketing_tactic",
  "gclid",
  "dclid",
  "fbclid",
  "msclkid",
]);

function invalidSourceUrl(): TypeError {
  return new TypeError("Manufacturer source URL must be a valid absolute HTTPS URL without credentials");
}

function compareStrings(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function normalizePathEncoding(pathname: string): string {
  return pathname.replace(/%[\da-f]{2}/gi, (encoded) => {
    const character = String.fromCharCode(Number.parseInt(encoded.slice(1), 16));
    // Decode only RFC 3986 unreserved characters. In particular, %2F stays an
    // encoded slash; decoding it would change the identity of a product path.
    return /^[a-z\d._~-]$/i.test(character) ? character : encoded.toUpperCase();
  });
}

/**
 * Stable identity for a manufacturer URL; this does not fetch or trust its host.
 *
 * HTTPS scheme/host/default port, dot segments, and unreserved path escapes are
 * normalized. Path case, trailing/repeated slashes, and reserved escapes remain
 * significant. Queries use URLSearchParams' UTF-8 form semantics and preserve
 * duplicate pairs, sorted by decoded key and then value (UTF-16 code units).
 * Only exact, case-sensitive tracking names above are removed.
 */
export function canonicalizeLuminaireSourceUrl(input: string): string {
  if (typeof input !== "string" || input.length === 0 || /[\s\\"<>`{}|^]/u.test(input)) {
    throw invalidSourceUrl();
  }
  for (const character of input) {
    const codePoint = character.codePointAt(0)!;
    if (codePoint < 0x20 || codePoint === 0x7f || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
      throw invalidSourceUrl();
    }
  }
  if (/%(?![\da-f]{2})/i.test(input)) throw invalidSourceUrl();

  // URL alone repairs inputs such as https:///host, backslashes, whitespace,
  // and empty userinfo. Reject those before allowing its normalizations.
  const authority = /^https:\/\/([^/?#]+)/i.exec(input)?.[1];
  if (!authority || authority.includes("@") || authority.endsWith(":")) throw invalidSourceUrl();

  let url: URL;
  try {
    url = new URL(input);
  } catch {
    throw invalidSourceUrl();
  }
  if (url.protocol !== "https:" || !url.hostname || url.username || url.password) throw invalidSourceUrl();

  // URLSearchParams silently replaces invalid UTF-8 with U+FFFD. Reject those
  // query bytes instead of merging distinct malformed inputs into one digest.
  try {
    decodeURIComponent(url.search);
  } catch {
    throw invalidSourceUrl();
  }

  const retainedPairs = [...url.searchParams.entries()]
    .filter(([key]) => !TRACKING_QUERY_PARAMETERS.has(key))
    .sort(([leftKey, leftValue], [rightKey, rightValue]) =>
      compareStrings(leftKey, rightKey) || compareStrings(leftValue, rightValue));
  const query = new URLSearchParams(retainedPairs).toString();
  return `${url.origin}${normalizePathEncoding(url.pathname)}${query ? `?${query}` : ""}`;
}

export function normalizeLuminaireUrlDigest(input: string): string {
  if (typeof input !== "string" || !/^(?:sha256:)?[\da-f]{64}$/i.test(input)) {
    throw new TypeError("Luminaire URL digest must be a SHA-256 digest with 64 hexadecimal characters");
  }
  return `sha256:${input.replace(/^sha256:/i, "").toLowerCase()}`;
}

export async function getLuminaireUrlIdentity(input: string): Promise<{
  canonicalUrl: string;
  urlDigest: string;
}> {
  const canonicalUrl = canonicalizeLuminaireSourceUrl(input);
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.subtle == null) {
    throw new Error("Web Crypto SHA-256 is required to identify a luminaire source URL");
  }
  const digest = await cryptoApi.subtle.digest("SHA-256", new TextEncoder().encode(canonicalUrl));
  const hexadecimal = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
  return { canonicalUrl, urlDigest: `sha256:${hexadecimal}` };
}
